// Reine Ableitungslogik der Anlagenüberwachung (Bereich, Grenzwert-Zustand, Alarm-Summe).
//
// Bewusst frei von React/DOM: aus Katalog-Variablen + Live-Tick-Werten (+ Katalog-Grenzen)
// werden clientseitig Bereichszuordnung, Grenzwert-Zustand je Variable und die Alarm-Zusammen-
// fassung berechnet. Das hält die Ansicht schlank und die Logik testbar.

import type { CatalogVariable, TickValue } from '../api/types';

// Grenzwert-Zustand einer Messgröße gegen die Katalog-Grenzen (Lo/LoLo/Hi/HiHi):
//   normal  – innerhalb aller Grenzen
//   warn    – Lo/Hi über-/unterschritten (Warnstufe)
//   alarm   – LoLo/HiHi über-/unterschritten (Alarmstufe)
//   unknown – kein Wert, schlechte Qualität oder keine Grenzen definiert
export type LimitState = 'normal' | 'warn' | 'alarm' | 'unknown';

// Bewertet einen Messwert gegen die Katalog-Grenzen. LoLo/HiHi haben Vorrang vor Lo/Hi.
export function evalLimit(variable: CatalogVariable, value: number | undefined): LimitState {
  const limits = variable.limits;
  if (value === undefined || Number.isNaN(value) || !limits) return 'unknown';
  const { lolo, lo, hi, hihi } = limits;
  if ((hihi !== undefined && value >= hihi) || (lolo !== undefined && value <= lolo)) return 'alarm';
  if ((hi !== undefined && value >= hi) || (lo !== undefined && value <= lo)) return 'warn';
  return 'normal';
}

// Eine bewertete Kachel: Katalog-Variable + jüngster Tick + abgeleiteter Grenzwert-Zustand.
export interface EvaluatedVar {
  variable: CatalogVariable;
  tick: TickValue | undefined;
  state: LimitState;
}

// Anlagenbereich aus einer Variable ableiten. Primär aus `source.symbol` (Symbol-Instanz aus
// dem Anlagenschema), sonst aus dem Tag-Präfix vor dem ersten Trennzeichen (`-`, `_`, `.`).
// Fällt fail-safe auf einen Sammelbereich zurück, nie undefiniert.
export function deriveArea(variable: CatalogVariable): string {
  const symbol = variable.source?.symbol?.trim();
  if (symbol) {
    // Instanz-Suffixe wie „reaktor-1" / „reaktor#2" auf den Bereichsnamen reduzieren.
    const base = symbol.split(/[#]/)[0].replace(/-\d+$/, '').trim();
    if (base) return base;
  }
  const prefix = variable.tag.split(/[-_.]/)[0].trim();
  return prefix || 'sonstige';
}

// Variablen nach Bereich gruppieren (stabile Reihenfolge nach erstem Auftreten).
export function groupByArea(variables: CatalogVariable[]): { area: string; variables: CatalogVariable[] }[] {
  const order: string[] = [];
  const buckets = new Map<string, CatalogVariable[]>();
  for (const v of variables) {
    const area = deriveArea(v);
    if (!buckets.has(area)) {
      buckets.set(area, []);
      order.push(area);
    }
    buckets.get(area)!.push(v);
  }
  return order.map((area) => ({ area, variables: buckets.get(area)! }));
}

// Aktuelle Alarm-/Warn-Summe über alle Messgrößen (Eingänge). unknown zählt nicht.
export interface AlarmSummary {
  alarms: number;
  warnings: number;
}

export function summarize(evaluated: EvaluatedVar[]): AlarmSummary {
  let alarms = 0;
  let warnings = 0;
  for (const e of evaluated) {
    if (e.state === 'alarm') alarms += 1;
    else if (e.state === 'warn') warnings += 1;
  }
  return { alarms, warnings };
}

// Katalog-Variablen mit dem jüngsten Tick verheiraten und den Grenzwert-Zustand ableiten.
export function evaluate(
  variables: CatalogVariable[],
  latest: Map<string, TickValue>,
): EvaluatedVar[] {
  return variables.map((variable) => {
    const tick = latest.get(variable.tag);
    return { variable, tick, state: evalLimit(variable, tick?.value) };
  });
}

// Regelschleifen-Modus eines Aktor-Sollwerts. Primär aus dem Tick-Feld `mode`, sonst defensiv
// unbekannt — hier dockt später der Safety-Status an, falls die WS heute nur ticks liefert.
export type LoopMode = 'auto' | 'hand' | 'failsafe' | 'unknown';

export function deriveMode(tick: TickValue | undefined): LoopMode {
  const m = tick?.mode;
  if (m === 'auto' || m === 'hand' || m === 'failsafe') return m;
  return 'unknown';
}

// Position eines Werts im Katalog-Bereich [min,max] als Anteil 0..1 (für den Bereichsbalken).
export function rangeFraction(variable: CatalogVariable, value: number | undefined): number | null {
  if (value === undefined || Number.isNaN(value)) return null;
  const { min, max } = variable.range;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  return Math.min(1, Math.max(0, (value - min) / (max - min)));
}

// Positionen der Warn-Grenzen (Lo/Hi, ersatzweise LoLo/HiHi) im Bereich [min,max] als Anteil 0..1 —
// für die grüne/rote Zoneneinteilung des Tacho-Instruments. null, wenn keine Grenze/kein Bereich.
export function limitFractions(
  variable: CatalogVariable,
): { loFrac: number | null; hiFrac: number | null } {
  const { min, max } = variable.range;
  const lim = variable.limits;
  const frac = (v: number | undefined): number | null =>
    v === undefined || !Number.isFinite(min) || !Number.isFinite(max) || max <= min
      ? null
      : Math.min(1, Math.max(0, (v - min) / (max - min)));
  return { loFrac: frac(lim?.lo ?? lim?.lolo), hiFrac: frac(lim?.hi ?? lim?.hihi) };
}
